import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { UserListParams } from '@/services/admin/userService'
import {
  merchantService,
  type MerchantChainReviewListParams,
  type MerchantListParams,
} from '@/services/admin/merchantService'
import type { MerchantChainCompatibility } from '@/types'
import { orderService, type OrderListParams } from '@/services/admin/orderService'
import { auditService, type AuditLogParams } from '@/services/admin/auditService'
import {
  economyService,
  type OnchainPolicyPlanOverrideListParams,
  type OnchainProfileListParams,
} from '@/services/admin/economyService'
import { systemService } from '@/services/admin/systemService'
import {
  blocklistService,
  type BlocklistListParams,
  type BlocklistCreatePayload,
  type BlocklistUpdatePayload,
  type BlocklistImportPayload,
} from '@/services/admin/blocklistService'
import {
  merchantSignupInviteService,
  type MerchantSignupAccessListParams,
  type MerchantSignupAccessCreatePayload,
  type MerchantSignupAccessUpdatePayload,
  type MerchantSignupAccessImportPayload,
  type MerchantSignupAccessGeneratePayload,
} from '@/services/admin/merchantSignupInviteService'
import {
  contractService,
  getAllContractDeploymentRecords,
  getAllContractRegistryRecords,
} from '@/services/admin/contractService'
import { settlementService } from '@/services/admin/settlementService'
import { refundService } from '@/services/admin/refundService'
import { userService, type UserLoginHistoryParams } from '@/services/admin/userService'
import { rescueService, type RescueTaskListParams } from '@/services/admin/rescueService'
import {
  announcementService,
  type AnnouncementListParams,
} from '@/services/admin/announcementService'
import { ContractRegistryListParams, ContractRegistryStatusUpdate, ContractDeploymentListParams, ContractInitializationQueryParams, BatchContractVerificationParams, ContractVerificationStatusParams, RefundOperationListParams, RefundOperationVerifyRequest, BatchVerifyRequest, MetaRefundRequestPayload, ExtendSweepUnlockPayload, UpdateOnchainOperationProfilePayload, CreateOnchainOperationProfilePayload, OnchainOperationProfile, OnchainOperationPolicyCapability, UpdateOnchainOperationPolicyPlanOverridePayload } from '@/types'
import { supportService, type SupportReceivesParams, type SupportTemplatePayload } from '@/services/admin/supportService'
import type { AnnouncementDetail } from '@/types'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildDashboardMetricsQueryOptions } from '@/navigation/queryOptions'

const useNetworkQueryKey = (key: readonly unknown[]) => {
  const { currentEnv } = useNetworkEnvironment()
  return [...key, currentEnv]
}

const isCurrentMerchantSignupInviteQuery = (
  queryKey: readonly unknown[],
  currentEnv: string
) =>
  queryKey[0] === 'system'
  && queryKey[1] === 'merchant-signup-invites'
  && queryKey[queryKey.length - 1] === currentEnv

// Dashboard hooks
export function useDashboardMetrics() {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    ...buildDashboardMetricsQueryOptions(currentEnv),
    refetchInterval: false, // Disabled auto-refresh
  })
}

// Admin User hooks
export function useAdminUsers(params?: UserListParams) {
  return useQuery({
    queryKey: ['admin-users', params],
    queryFn: () => userService.getAllAdminUsers(params),
    placeholderData: (previousData) => previousData,
    refetchInterval: false, // Disabled auto-refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  })
}

export function useSupportFormTemplate(options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['support', 'ticket-template']),
    queryFn: () => supportService.getSupportFormTemplate(currentEnv),
    staleTime: 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useUpdateSupportFormTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: SupportTemplatePayload) =>
      supportService.updateSupportFormTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support', 'ticket-template'] })
    },
  })
}

export function useSupportReceives(
  params?: SupportReceivesParams,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['support', 'receives', params]),
    queryFn: () => supportService.listSupportReceives(params, currentEnv),
    placeholderData: (previousData) => previousData,
    staleTime: 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useUpdateSupportReceive() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { ticketStatus?: string; supportNotes?: string | null } }) =>
      supportService.updateSupportReceive(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support', 'receives'] })
    },
  })
}

export function useAnnouncements(params?: AnnouncementListParams) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['announcements', params]),
    queryFn: () => announcementService.listAnnouncements(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60 * 1000,
  })
}

export function useAnnouncement(announcementId?: string, options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery<AnnouncementDetail>({
    queryKey: useNetworkQueryKey(['announcement', announcementId]),
    queryFn: () => announcementService.getAnnouncement(announcementId ?? '', currentEnv),
    enabled: Boolean(announcementId) && (options?.enabled ?? true),
  })
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      announcementService.createAnnouncement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
    },
  })
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      announcementService.updateAnnouncement(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      queryClient.invalidateQueries({ queryKey: ['announcement', variables.id] })
    },
  })
}

export function usePublishAnnouncement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      announcementService.publishAnnouncement(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      queryClient.invalidateQueries({ queryKey: ['announcement', variables.id] })
    },
  })
}

export function useRevokeAnnouncement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => announcementService.revokeAnnouncement(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      queryClient.invalidateQueries({ queryKey: ['announcement', id] })
    },
  })
}

export function useAdminRoles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => userService.getRoles(),
    staleTime: 5 * 60 * 1000, // Roles rarely change, cache for 5 minutes
    enabled: options?.enabled ?? true,
  })
}

export function useAdminUser(userId: string) {
  return useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => userService.getAdminUserById(userId),
    enabled: !!userId,
  })
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: { email: string; username: string; password: string; role_id: string }) =>
      userService.createAdminUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateAdminUser() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ userId, data }: { 
      userId: string; 
      data: { email?: string; username?: string; role_id?: string } 
    }) => userService.updateAdminUser(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin-user', variables.userId] })
    },
  })
}

export function useChangeAdminPassword() {
  return useMutation({
    mutationFn: ({ userId, data }: { 
      userId: string; 
      data: { new_password: string; current_password?: string } 
    }) => userService.changeAdminUserPassword(userId, data),
  })
}

export function useUpdateAdminUserStatus() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ userId, data }: { 
      userId: string; 
      data: { is_active: boolean; reason?: string } 
    }) => userService.updateAdminUserStatus(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin-user', variables.userId] })
    },
  })
}

// Merchant hooks
export function useMerchants(params?: MerchantListParams) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['merchants', params]),
    queryFn: () => merchantService.getAllMerchants(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false, // Disabled auto-refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  })
}

export function useMerchant(merchantId: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['merchant', merchantId]),
    queryFn: () => merchantService.getMerchantById(merchantId, currentEnv),
    enabled: !!merchantId,
  })
}

export function useMerchantOrderDefaults(
  merchantId: string,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['merchant-order-defaults', merchantId]),
    queryFn: () => merchantService.getMerchantOrderDefaults(merchantId, currentEnv),
    enabled: !!merchantId && (options?.enabled ?? true),
    staleTime: 60 * 1000,
  })
}

export function useRefreshMerchantOrderDefaults() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({ merchantId }: { merchantId: string }) =>
      merchantService.refreshMerchantOrderDefaults(merchantId, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['merchant-order-defaults', variables.merchantId] })
      queryClient.invalidateQueries({ queryKey: ['merchant', variables.merchantId] })
      queryClient.invalidateQueries({ queryKey: ['merchants'] })
    },
  })
}

export function useRecomputeMerchantSnapshot() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({ merchantId }: { merchantId: string }) =>
      merchantService.recomputeMerchantSnapshot(merchantId, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['merchant-order-defaults', variables.merchantId] })
      queryClient.invalidateQueries({ queryKey: ['merchant', variables.merchantId] })
      queryClient.invalidateQueries({ queryKey: ['merchants'] })
    },
  })
}

export function useUpdateMerchantStatus() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  
  return useMutation({
    mutationFn: ({ merchantId, status, reason }: { merchantId: string; status: string; reason?: string }) =>
      merchantService.updateMerchantStatus(merchantId, status, reason, currentEnv),
    onSuccess: () => {
      // Invalidate and refetch merchant data
      queryClient.invalidateQueries({ queryKey: ['merchants'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useMerchantChainReviews(params?: MerchantChainReviewListParams) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['merchant-chain-reviews', params]),
    queryFn: () => merchantService.getMerchantChainReviews(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60 * 1000,
  })
}

export function useMerchantChains(
  merchantId: string,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['merchant-chains', merchantId]),
    queryFn: () => merchantService.getMerchantChains(merchantId, currentEnv),
    enabled: !!merchantId && (options?.enabled ?? true),
    staleTime: 60 * 1000,
  })
}

export function useApproveMerchantChain() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({
      merchantId,
      compatibility,
      reviewRevisionId,
    }: {
      merchantId: string
      compatibility: MerchantChainCompatibility
      reviewRevisionId: string
    }) => merchantService.approveMerchantChain(merchantId, compatibility, reviewRevisionId, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['merchant-chain-reviews'] })
      queryClient.invalidateQueries({ queryKey: ['merchant-chains', variables.merchantId] })
      queryClient.invalidateQueries({ queryKey: ['merchant', variables.merchantId] })
      queryClient.invalidateQueries({ queryKey: ['merchants'] })
    },
  })
}

export function useRejectMerchantChain() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({
      merchantId,
      compatibility,
      reason,
      reviewRevisionId,
    }: {
      merchantId: string
      compatibility: MerchantChainCompatibility
      reason: string
      reviewRevisionId: string
    }) => merchantService.rejectMerchantChain(merchantId, compatibility, reason, reviewRevisionId, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['merchant-chain-reviews'] })
      queryClient.invalidateQueries({ queryKey: ['merchant-chains', variables.merchantId] })
      queryClient.invalidateQueries({ queryKey: ['merchant', variables.merchantId] })
      queryClient.invalidateQueries({ queryKey: ['merchants'] })
    },
  })
}

// Order hooks
export function useOrders(params?: OrderListParams) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['admin', 'orders', params]),
    queryFn: () => orderService.getAllOrders(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false, // Disabled auto-refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 2 * 60 * 1000, // Data is fresh for 2 minutes (orders change more frequently)
  })
}

export function useOrderLifecycle(orderId?: string | null, include?: string[], options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  const includeKey = include && include.length ? include.join(',') : undefined
  return useQuery({
    queryKey: useNetworkQueryKey(['admin', 'orders', 'lifecycle', orderId ?? null, includeKey]),
    queryFn: () => orderService.getOrderLifecycle(orderId as string, include, currentEnv),
    enabled: Boolean(orderId) && (options?.enabled ?? true),
    staleTime: 60 * 1000,
  })
}

// Audit log hooks
export function useAuditLogs(params?: AuditLogParams, options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['audit-logs', params]),
    queryFn: () => auditService.getAuditLogs(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false, // Disabled auto-refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 3 * 60 * 1000, // Data is fresh for 3 minutes
    enabled: options?.enabled ?? true,
  })
}

// Rescue task hooks
export function useRescueTasks(params?: RescueTaskListParams) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['rescue-tasks', params]),
    queryFn: () => rescueService.getRescueTasks(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60 * 1000,
  })
}

// System status hooks
export function useSystemStatus() {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['system', 'status']),
    queryFn: () => systemService.getSystemStatus(currentEnv),
    refetchInterval: false, // Disabled auto-refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  })
}

export function useBlocklistEntries(
  params?: BlocklistListParams,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['system', 'blocklist', params]),
    queryFn: () => blocklistService.list(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useCreateBlocklistEntry() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: BlocklistCreatePayload) =>
      blocklistService.create(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system', 'blocklist'] })
    },
  })
}

export function useUpdateBlocklistEntry() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: ({
      entryId,
      payload,
    }: {
      entryId: string
      payload: BlocklistUpdatePayload
    }) => blocklistService.update(entryId, payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system', 'blocklist'] })
    },
  })
}

export function useDeleteBlocklistEntry() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (entryId: string) => blocklistService.delete(entryId, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system', 'blocklist'] })
    },
  })
}

export function useImportBlocklistEntries() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: BlocklistImportPayload) =>
      blocklistService.import(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system', 'blocklist'] })
    },
  })
}

export function useMerchantSignupInviteEntries(
  params?: MerchantSignupAccessListParams,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['system', 'merchant-signup-invites', params]),
    queryFn: () => merchantSignupInviteService.list(params, currentEnv),
    placeholderData: previousData => previousData,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useCreateMerchantSignupInviteEntry() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: MerchantSignupAccessCreatePayload) =>
      merchantSignupInviteService.create(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query => isCurrentMerchantSignupInviteQuery(query.queryKey, currentEnv),
      })
    },
  })
}

export function useUpdateMerchantSignupInviteEntry() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: ({
      entryId,
      payload,
    }: {
      entryId: string
      payload: MerchantSignupAccessUpdatePayload
    }) => merchantSignupInviteService.update(entryId, payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query => isCurrentMerchantSignupInviteQuery(query.queryKey, currentEnv),
      })
    },
  })
}

export function useDeleteMerchantSignupInviteEntry() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (entryId: string) =>
      merchantSignupInviteService.delete(entryId, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query => isCurrentMerchantSignupInviteQuery(query.queryKey, currentEnv),
      })
    },
  })
}

export function useImportMerchantSignupInviteEntries() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: MerchantSignupAccessImportPayload) =>
      merchantSignupInviteService.import(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query => isCurrentMerchantSignupInviteQuery(query.queryKey, currentEnv),
      })
    },
  })
}

export function useGenerateMerchantSignupInviteEntries() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: (payload: MerchantSignupAccessGeneratePayload) =>
      merchantSignupInviteService.generate(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query => isCurrentMerchantSignupInviteQuery(query.queryKey, currentEnv),
      })
    },
  })
}

export function useEconomyStatus() {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['economy', 'status']),
    queryFn: () => economyService.getEconomyStatus(currentEnv),
    refetchInterval: false, // Disabled auto-refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  })
}

export function useOnchainProfiles(
  params?: OnchainProfileListParams,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery<OnchainOperationProfile[]>({
    queryKey: useNetworkQueryKey(['system', 'onchain-profiles', params]),
    queryFn: () => economyService.getOnchainOperationProfiles(params, currentEnv),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: options?.enabled ?? true,
  })
}

export function useOnchainProfile(profileId?: string, options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['system', 'onchain-profile', profileId ?? null]),
    queryFn: () => economyService.getOnchainOperationProfile(profileId as string, currentEnv),
    enabled: Boolean(profileId) && (options?.enabled ?? true),
  })
}

export function useUpdateOnchainProfile() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({ profileId, data }: { profileId: string; data: UpdateOnchainOperationProfilePayload }) =>
      economyService.updateOnchainOperationProfile(profileId, data, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['system', 'onchain-profiles'] })
      queryClient.invalidateQueries({ queryKey: ['system', 'onchain-profile', variables.profileId] })
    },
  })
}

export function useCreateOnchainProfile() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: (data: CreateOnchainOperationProfilePayload) =>
      economyService.createOnchainOperationProfile(data, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system', 'onchain-profiles'] })
      queryClient.invalidateQueries({ queryKey: ['system', 'onchain-policy-capabilities'] })
    },
  })
}

export function useOnchainPolicyPlanOverrides(
  params?: OnchainPolicyPlanOverrideListParams,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['system', 'onchain-policy-plan-overrides', params]),
    queryFn: () => economyService.getOnchainOperationPolicyPlanOverrides(params, currentEnv),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: options?.enabled ?? true,
  })
}

export function useOnchainOperationPolicyCapabilities(options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery<OnchainOperationPolicyCapability[]>({
    queryKey: useNetworkQueryKey(['system', 'onchain-policy-capabilities']),
    queryFn: () => economyService.getOnchainOperationPolicyCapabilities(currentEnv),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: options?.enabled ?? true,
  })
}

export function useOnchainPolicyPlanOverride(overrideId?: string, options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['system', 'onchain-policy-plan-override', overrideId ?? null]),
    queryFn: () => economyService.getOnchainOperationPolicyPlanOverride(overrideId as string, currentEnv),
    enabled: Boolean(overrideId) && (options?.enabled ?? true),
  })
}

export function useCreateOnchainPolicyPlanOverride() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: (data: UpdateOnchainOperationPolicyPlanOverridePayload) =>
      economyService.createOnchainOperationPolicyPlanOverride(data, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system', 'onchain-policy-plan-overrides'] })
    },
  })
}

export function useUpdateOnchainPolicyPlanOverride() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({
      overrideId,
      data,
    }: {
      overrideId: string
      data: UpdateOnchainOperationPolicyPlanOverridePayload
    }) => economyService.updateOnchainOperationPolicyPlanOverride(overrideId, data, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['system', 'onchain-policy-plan-overrides'] })
      queryClient.invalidateQueries({
        queryKey: ['system', 'onchain-policy-plan-override', variables.overrideId],
      })
    },
  })
}

export function useSetOnchainPolicyPlanOverrideActive() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({
      overrideId,
      isActive,
      expectedRevision,
    }: {
      overrideId: string
      isActive: boolean
      expectedRevision: number
    }) => economyService.setOnchainOperationPolicyPlanOverrideActive(
      overrideId,
      isActive,
      expectedRevision,
      currentEnv
    ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['system', 'onchain-policy-plan-overrides'] })
      queryClient.invalidateQueries({
        queryKey: ['system', 'onchain-policy-plan-override', variables.overrideId],
      })
    },
  })
}

export function useDeleteOnchainPolicyPlanOverride() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: (overrideId: string) =>
      economyService.deleteOnchainOperationPolicyPlanOverride(overrideId, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system', 'onchain-policy-plan-overrides'] })
    },
  })
}

export function useRefreshOnchainPolicyCache() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  return useMutation({
    mutationFn: () => economyService.refreshOnchainOperationPolicyCache(currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'system' &&
          (query.queryKey[1] === 'onchain-profiles' || query.queryKey[1] === 'onchain-profile') &&
          query.queryKey[query.queryKey.length - 1] === currentEnv,
      })
    },
  })
}

export function useRpcHealth(refetchInterval: number = 60 * 1000) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['system', 'rpcHealth']),
    queryFn: () => systemService.getRpcHealth(currentEnv),
    refetchInterval,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 30 * 1000,
    retry: 2,
  })
}

export function useUpdateRpcPriority() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  const rpcHealthKey = useNetworkQueryKey(['system', 'rpcHealth'])

  return useMutation({
    mutationFn: (payload: { chainKey: string; endpointOrder?: string[]; reset?: boolean }) =>
      systemService.updateRpcPriority(payload, currentEnv),
    onSuccess: (data) => {
      queryClient.setQueryData(rpcHealthKey, data)
    },
  })
}

export function useUpdateRpcPriorityBulk() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  const rpcHealthKey = useNetworkQueryKey(['system', 'rpcHealth'])

  return useMutation({
    mutationFn: (payload: { mode: 'reset' } | { mode: 'reorder'; providerOrder: string[] }) =>
      systemService.updateRpcPriorityBulk(payload, currentEnv),
    onSuccess: (data) => {
      queryClient.setQueryData(rpcHealthKey, data)
    },
  })
}

export function useUpdateRpcEndpointStatus() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  const rpcHealthKey = useNetworkQueryKey(['system', 'rpcHealth'])

  return useMutation({
    mutationFn: (payload: { chainKey: string; endpointId: string; disabled: boolean }) =>
      systemService.updateRpcEndpointStatus(payload, currentEnv),
    onSuccess: (data) => {
      queryClient.setQueryData(rpcHealthKey, data)
    },
  })
}

// Settlement hooks
export function useSettlementGuard() {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['settlements', 'guard']),
    queryFn: () => settlementService.getSettlementGuard(currentEnv),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60 * 1000,
  })
}

export function useExtendSweepUnlock() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: ExtendSweepUnlockPayload }) =>
      settlementService.extendSweepUnlock(orderId, data, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] })
      queryClient.invalidateQueries({ queryKey: ['settlements', 'records'] })
      queryClient.invalidateQueries({ queryKey: ['settlements', 'guard'] })
    },
  })
}

export function useEnqueueSettlements() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: (payload: {
      orderIds?: string[]
      limit?: number
      force?: boolean
      forceReason?: string
    }) =>
      settlementService.enqueueSettlements(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements', 'records'] })
      queryClient.invalidateQueries({ queryKey: ['settlements', 'guard'] })
    },
  })
}

export function useEnqueueSettlementVerification() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: (payload: { orderIds?: string[]; chainId?: string; limit?: number; force?: boolean }) =>
      settlementService.enqueueSettlementVerification(payload, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements', 'records'] })
      queryClient.invalidateQueries({ queryKey: ['settlements', 'eligible'] })
    },
  })
}

export function useTriggerSettlement() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: (payload: { orderId: string; force?: boolean; forceReason?: string }) =>
      settlementService.triggerSettlement(payload.orderId, {
        force: payload.force,
        forceReason: payload.forceReason,
      }, currentEnv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements', 'eligible'] })
      queryClient.invalidateQueries({ queryKey: ['settlements', 'records'] })
    },
  })
}

export function useUsers(params?: UserListParams) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['platform-users', params]),
    queryFn: () => userService.getAllUsers(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false, // Disabled auto-refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  })
}

export function useUser(userId: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['platform-user', userId]),
    queryFn: () => userService.getUserById(userId, currentEnv),
    enabled: !!userId,
  })
}

export function useUserLoginHistory(
  userId: string,
  params?: UserLoginHistoryParams,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['platform-user-login-history', userId, params]),
    queryFn: () => userService.getUserLoginHistory(userId, params, currentEnv),
    enabled: Boolean(userId) && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
  })
}

export function useRefreshUserMerchantOrderDefaults() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      userService.refreshUserMerchantOrderDefaults(userId, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['platform-user', variables.userId] })
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      queryClient.invalidateQueries({ queryKey: ['merchant-order-defaults'] })
      queryClient.invalidateQueries({ queryKey: ['merchant'] })
      queryClient.invalidateQueries({ queryKey: ['merchants'] })
    },
  })
}

export function useRecomputeUserMerchantSnapshots() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      userService.recomputeUserMerchantSnapshots(userId, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['platform-user', variables.userId] })
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      queryClient.invalidateQueries({ queryKey: ['merchant-order-defaults'] })
      queryClient.invalidateQueries({ queryKey: ['merchant'] })
      queryClient.invalidateQueries({ queryKey: ['merchants'] })
    },
  })
}

export function useUpdatePlatformUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, data }: {
      userId: string
      data: { email?: string; username?: string; is_active?: boolean; is_email_verified?: boolean }
    }) =>
      userService.updatePlatformUser(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      queryClient.invalidateQueries({ queryKey: ['platform-user', variables.userId] })
    },
  })
}

export function useResetPlatformUserMfa() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      userService.resetPlatformUserMfa(userId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      queryClient.invalidateQueries({ queryKey: ['platform-user', variables.userId] })
    },
  })
}

export function useUpdatePlatformUserStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: { is_active: boolean; reason?: string } }) =>
      userService.updatePlatformUserStatus(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      queryClient.invalidateQueries({ queryKey: ['platform-user', variables.userId] })
    },
  })
}

export function useChangePlatformUserPlan() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string
      data: { planId: string; endsAt?: string | null; reason: string }
    }) => userService.changePlatformUserPlan(userId, data, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['platform-user', variables.userId] })
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      queryClient.invalidateQueries({ queryKey: ['merchant-order-defaults'] })
      queryClient.invalidateQueries({ queryKey: ['merchant'] })
      queryClient.invalidateQueries({ queryKey: ['merchants'] })
    },
  })
}

// Contract Registry hooks
export function useContractRegistryRecords(
  params?: ContractRegistryListParams,
  options?: { enabled?: boolean; fetchAll?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey([
      'contract-registry',
      'records',
      params,
      options?.fetchAll ? 'all-pages' : 'single-page',
    ]),
    queryFn: () => options?.fetchAll
      ? getAllContractRegistryRecords(params, currentEnv)
      : contractService.getContractRegistryRecords(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 2 * 60 * 1000, // Data is fresh for 2 minutes
    enabled: options?.enabled ?? true,
  })
}

export function useContractRegistryRecord(id: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['contract-registry', 'record', id]),
    queryFn: () => contractService.getContractRegistryRecord(id, currentEnv),
    enabled: !!id,
  })
}

export function useUpdateContractRegistryStatus() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ContractRegistryStatusUpdate }) =>
      contractService.updateContractRegistryStatus(id, data, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contract-registry', 'records'] })
      queryClient.invalidateQueries({ queryKey: ['contract-registry', 'record', variables.id] })
    },
  })
}

export function useDeployContractRegistry() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      contractService.deployContractRegistry(id, currentEnv),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contract-registry', 'records'] })
      queryClient.invalidateQueries({ queryKey: ['contract-registry', 'record', variables.id] })
    },
  })
}

// Contract Deployment Records hooks
export function useContractDeploymentRecords(
  params?: ContractDeploymentListParams,
  options?: { fetchAll?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey([
      'admin',
      'contract-deployment',
      'records',
      params,
      options?.fetchAll ? 'all-pages' : 'single-page',
    ]),
    queryFn: () => options?.fetchAll
      ? getAllContractDeploymentRecords(params, currentEnv)
      : contractService.getContractDeploymentRecords(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    staleTime: 2 * 60 * 1000, // Data is fresh for 2 minutes
  })
}

export function useContractDeploymentRecord(id: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['admin', 'contract-deployment', 'record', id]),
    queryFn: () => contractService.getContractDeploymentRecord(id, currentEnv),
    enabled: !!id,
  })
}

// Contract Initialization hooks
export function useContractInitializationStatus(orderBusinessId: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['contract-initialization', 'status', orderBusinessId]),
    queryFn: () => contractService.getContractInitializationStatus(orderBusinessId, currentEnv),
    enabled: !!orderBusinessId,
    staleTime: 30 * 1000, // Data is fresh for 30 seconds
  })
}

export function useContractInitializationStatusByAddress(contractAddress: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['contract-initialization', 'status-by-address', contractAddress]),
    queryFn: () => contractService.getContractInitializationStatusByAddress(contractAddress, currentEnv),
    enabled: !!contractAddress,
    staleTime: 30 * 1000, // Data is fresh for 30 seconds
  })
}

export function useUninitializedContracts(params?: ContractInitializationQueryParams) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['contract-initialization', 'uninitialized', params]),
    queryFn: () => contractService.getUninitializedContracts(params, currentEnv),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1 * 60 * 1000, // Data is fresh for 1 minute
  })
}

export function useRetryContractInitialization() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  
  return useMutation({
    mutationFn: ({ orderBusinessId, data }: { orderBusinessId: string; data?: { force?: boolean } }) =>
      contractService.retryContractInitialization(orderBusinessId, data, currentEnv),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['contract-initialization', 'status', variables.orderBusinessId] })
      queryClient.invalidateQueries({ queryKey: ['contract-initialization', 'uninitialized'] })
      queryClient.invalidateQueries({ queryKey: ['contract-registry', 'records'] })
    },
  })
}

// Contract Verification hooks

// Get contracts needing verification
export function useContractsNeedingVerification(params?: {
  chainId?: string
  limit?: number
  offset?: number
}) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['contracts-needing-verification', params]),
    queryFn: () => contractService.getContractsNeedingVerification(params, currentEnv),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1 * 60 * 1000, // Data is fresh for 1 minute
  })
}

// Get contract verification status
export function useContractVerificationStatus(params?: ContractVerificationStatusParams) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['admin', 'contract-verification', 'status', params]),
    queryFn: () => contractService.getContractVerificationStatus(params, currentEnv),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1 * 60 * 1000, // Data is fresh for 1 minute
  })
}

// Queue single contract verification
export function useQueueContractVerification() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  
  return useMutation({
    mutationFn: (deploymentId: string) =>
      contractService.queueContractVerification(deploymentId, currentEnv),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'contract-verification', 'status'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'contract-deployment', 'records'] })
    },
  })
}

// Queue batch contract verification
export function useQueueBatchContractVerification() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: (params: BatchContractVerificationParams) =>
      contractService.queueBatchContractVerification(params, currentEnv),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'contract-verification', 'status'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'contract-deployment', 'records'] })
    },
  })
}

// Refund Operations hooks
export function useRefundOperations(params?: RefundOperationListParams) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['admin', 'refund-operations', params]),
    queryFn: () => refundService.getAllRefundOperations(params, currentEnv),
    placeholderData: (previousData) => previousData,
    refetchInterval: false, // Disabled auto-refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 2 * 60 * 1000, // Data is fresh for 2 minutes (refund operations change moderately)
  })
}

export function useRefundOperation(id: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['admin', 'refund-operation', id]),
    queryFn: () => refundService.getRefundOperationById(id, currentEnv),
    enabled: !!id,
    staleTime: 30 * 1000, // Data is fresh for 30 seconds
  })
}

export function useOrderRefundData(orderId?: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['admin', 'order-refund-data', orderId]),
    queryFn: () => refundService.getOrderRefundData(orderId!, currentEnv),
    enabled: Boolean(orderId),
    staleTime: 60 * 1000,
  })
}

export function useOrderRefundHistory(orderId?: string) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['admin', 'order-refund-history', orderId]),
    queryFn: () => refundService.getOrderRefundHistory(orderId!, currentEnv),
    enabled: Boolean(orderId),
    staleTime: 60 * 1000,
  })
}

export function useVerifyRefundOperation() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: RefundOperationVerifyRequest }) =>
      refundService.verifyRefundOperation(id, data, currentEnv),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'refund-operations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'refund-operation', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useVerifyRefundBatch() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: (request: BatchVerifyRequest) =>
      refundService.verifyRefundBatch(request, currentEnv),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'refund-operations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'refund-statistics'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useMetaRefundMutation() {
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()

  return useMutation({
    mutationFn: ({ orderId, payload }: { orderId: string; payload: MetaRefundRequestPayload }) =>
      refundService.executeMetaRefund(orderId, payload, currentEnv),
    onSuccess: (data, variables) => {
      const shouldRefresh = data?.success || (!!data?.txHash)
      if (!shouldRefresh) {
        return
      }

      queryClient.invalidateQueries({ queryKey: ['admin', 'order-refund-data', variables.orderId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'order-refund-history', variables.orderId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'refund-operations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'contract-deployment', 'records'] })
    },
  })
}

export function useRefundStatistics() {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['admin', 'refund-statistics']),
    queryFn: () => refundService.getRefundStatistics(currentEnv),
    refetchInterval: false, // Disabled auto-refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  })
}
export function useSettlementRecords(params?: {
  page?: number
  limit?: number
  status?: string
  order_id?: string
}) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: useNetworkQueryKey(['settlements', 'records', params]),
    queryFn: () => settlementService.getSettlementRecords(params, currentEnv),
    placeholderData: (previous) => previous,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 30 * 1000,
  })
}
